import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Calculator, CheckCircle2, Download, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { computeFiling, money } from "@/lib/tax";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const init = {
  business_name: "",
  trade_name: "",
  address: "",
  telephone: "",
  license_number: "",
  nature_changed: false,
  wholesale_2025: "",
  retail_2025: "",
  biz_2025: "",
  estimated_tax_paid_2025: "",
  wholesale_2026: "",
  retail_2026: "",
  biz_2026: "",
  cat_wholesale: false,
  cat_retail: false,
  cat_biz: false,
  paid_late: false,
  months_late: "",
  signed_name: "",
  official_title: "",
  preparer_name: "",
  preparer_phone: "",
};

const SectionCard = ({ title, subtitle, children }) => (
  <section className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
    <div className="border-l-4 border-[#1E3A8A] pl-4 mb-6">
      <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-medium text-slate-900">
        {title}
      </h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const MoneyInput = ({ label, value, onChange, testId, note }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-data">$</span>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="pl-7 font-mono-data"
        placeholder="0.00"
      />
    </div>
    {note && <p className="text-xs text-slate-400">{note}</p>}
  </div>
);

export default function NewFiling() {
  const navigate = useNavigate();
  const [f, setF] = useState(init);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [payMethod, setPayMethod] = useState("online"); // online | mail
  const [taxCfg, setTaxCfg] = useState(null);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const t = useMemo(() => computeFiling(f, taxCfg || {}), [f, taxCfg]);

  useEffect(() => {
    api
      .get("/settings/payments")
      .then((res) => setPaymentsEnabled(res.data.payments_enabled !== false))
      .catch(() => {});
    api
      .get("/settings/taxconfig")
      .then((res) => setTaxCfg(res.data))
      .catch(() => {});
  }, []);

  const buildPayload = () => ({
    ...f,
    wholesale_2025: parseFloat(f.wholesale_2025) || 0,
    retail_2025: parseFloat(f.retail_2025) || 0,
    biz_2025: parseFloat(f.biz_2025) || 0,
    estimated_tax_paid_2025: parseFloat(f.estimated_tax_paid_2025) || 0,
    wholesale_2026: parseFloat(f.wholesale_2026) || 0,
    retail_2026: parseFloat(f.retail_2026) || 0,
    biz_2026: parseFloat(f.biz_2026) || 0,
    months_late: parseInt(f.months_late) || 0,
  });

  const downloadDraft = async () => {
    setDownloading(true);
    try {
      const res = await api.post("/filings/preview-pdf", buildPayload(), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Cheltenham-Tax-Filing-DRAFT.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Draft PDF downloaded (not submitted)");
    } catch (err) {
      toast.error("Could not generate draft PDF");
    } finally {
      setDownloading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name || !f.address || !f.signed_name) {
      toast.error("Please complete business name, address, and signature.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/filings", buildPayload());
      const payOnline = paymentsEnabled && payMethod === "online" && (data.totals?.l14 || 0) > 0;
      if (payOnline) {
        try {
          const res = await api.post("/payments/checkout", {
            filing_id: data.id,
            origin_url: window.location.origin,
          });
          window.location.href = res.data.checkout_url;
          return;
        } catch (err) {
          toast.error("Filing saved, but payment could not start. You can pay from the filing page.");
          navigate(`/filing/${data.id}`);
          return;
        }
      }
      toast.success("Filing submitted — the Finance Office has been notified.");
      navigate(`/filing/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const Row = ({ line, label, gross, tax, muted }) => (
    <div
      className={`flex items-center justify-between py-2 text-sm ${
        muted ? "text-slate-500" : "text-slate-800"
      }`}
    >
      <span>
        <span className="font-mono-data text-slate-400 mr-2">{line}</span>
        {label}
      </span>
      <span className="flex items-center gap-6">
        {gross != null && <span className="font-mono-data text-slate-400">{money(gross)}</span>}
        <span className="font-mono-data font-medium w-24 text-right">{money(tax)}</span>
      </span>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900">
          Mercantile &amp; Business Privilege Tax Return
        </h1>
        <p className="mt-2 text-slate-500">
          Final Return 2025 &amp; Estimated Return 2026 · Due April 18, 2026
        </p>

        <form onSubmit={submit} className="mt-8 grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
            <SectionCard title="Business Information">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Business / Taxpayer Name *</Label>
                  <Input value={f.business_name} onChange={(e) => set("business_name")(e.target.value)} data-testid="input-business-name" placeholder="MEL MONEY" />
                </div>
                <div className="space-y-2">
                  <Label>Trade Name</Label>
                  <Input value={f.trade_name} onChange={(e) => set("trade_name")(e.target.value)} data-testid="input-trade-name" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Township Business Address *</Label>
                  <Input value={f.address} onChange={(e) => set("address")(e.target.value)} data-testid="input-address" placeholder="2019 66 Good St., Elkins Park, PA 19027" />
                </div>
                <div className="space-y-2">
                  <Label>Telephone</Label>
                  <Input value={f.telephone} onChange={(e) => set("telephone")(e.target.value)} data-testid="input-telephone" />
                </div>
                <div className="space-y-2">
                  <Label>Tax License Number</Label>
                  <Input value={f.license_number} onChange={(e) => set("license_number")(e.target.value)} data-testid="input-license-number" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-5 p-3 bg-slate-50 rounded-md">
                <Switch checked={f.nature_changed} onCheckedChange={set("nature_changed")} data-testid="switch-nature-changed" />
                <Label className="cursor-pointer">Nature of business changed during the year?</Label>
              </div>
            </SectionCard>

            <SectionCard title="Section 1 — Final Return 2025" subtitle="Actual gross receipts for 2025 (per Federal Income Tax Return).">
              <div className="grid sm:grid-cols-2 gap-5">
                <MoneyInput label="1a. Wholesale Mercantile (× .0010)" value={f.wholesale_2025} onChange={set("wholesale_2025")} testId="input-wholesale-2025" />
                <MoneyInput label="1b. Retail Mercantile (× .0015)" value={f.retail_2025} onChange={set("retail_2025")} testId="input-retail-2025" />
                <MoneyInput label="1c. Business Privilege (× .0040)" value={f.biz_2025} onChange={set("biz_2025")} testId="input-biz-2025" />
                <MoneyInput label="Line 3. Estimated Tax Paid in 2025" value={f.estimated_tax_paid_2025} onChange={set("estimated_tax_paid_2025")} testId="input-est-paid-2025" note="Credit toward 2025 tax due." />
              </div>
            </SectionCard>

            <SectionCard title="Section 2 — Estimated Return 2026" subtitle="Estimated gross receipts for 2026 (must be at least equal to 2025).">
              <div className="grid sm:grid-cols-2 gap-5">
                <MoneyInput label="8a. Wholesale Mercantile (× .0010)" value={f.wholesale_2026} onChange={set("wholesale_2026")} testId="input-wholesale-2026" />
                <MoneyInput label="8b. Retail Mercantile (× .0015)" value={f.retail_2026} onChange={set("retail_2026")} testId="input-retail-2026" />
                <MoneyInput label="8c. Business Privilege (× .0040)" value={f.biz_2026} onChange={set("biz_2026")} testId="input-biz-2026" />
              </div>
              <div className="mt-6">
                <Label className="mb-3 block">License Fee — $15 per applicable category (Line 12)</Label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    ["cat_wholesale", "Wholesale"],
                    ["cat_retail", "Retail"],
                    ["cat_biz", "Business Privilege"],
                  ].map(([k, lbl]) => (
                    <label key={k} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:border-[#1E3A8A] transition-colors">
                      <Checkbox checked={f[k]} onCheckedChange={set(k)} data-testid={`checkbox-${k}`} />
                      <span className="text-sm text-slate-700">{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Penalty &amp; Interest" subtitle="Applies if paid after April 18, 2026.">
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <Switch checked={f.paid_late} onCheckedChange={set("paid_late")} data-testid="switch-paid-late" />
                <Label className="cursor-pointer text-amber-900">Will this be paid after April 18, 2026?</Label>
              </div>
              {f.paid_late && (
                <div className="mt-5 max-w-xs space-y-2">
                  <Label>Months late (interest = 1.25%/month)</Label>
                  <Input type="number" min="0" value={f.months_late} onChange={(e) => set("months_late")(e.target.value)} data-testid="input-months-late" className="font-mono-data" placeholder="0" />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Declaration &amp; Signature" subtitle="I declare under penalty of law that all statements are true, correct and complete.">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Signature (type full name) *</Label>
                  <Input value={f.signed_name} onChange={(e) => set("signed_name")(e.target.value)} data-testid="input-signed-name" />
                </div>
                <div className="space-y-2">
                  <Label>Official Title (Owner, Partner…)</Label>
                  <Input value={f.official_title} onChange={(e) => set("official_title")(e.target.value)} data-testid="input-official-title" />
                </div>
                <div className="space-y-2">
                  <Label>Preparer Name (if not taxpayer)</Label>
                  <Input value={f.preparer_name} onChange={(e) => set("preparer_name")(e.target.value)} data-testid="input-preparer-name" />
                </div>
                <div className="space-y-2">
                  <Label>Preparer Phone</Label>
                  <Input value={f.preparer_phone} onChange={(e) => set("preparer_phone")(e.target.value)} data-testid="input-preparer-phone" />
                </div>
              </div>
            </SectionCard>
          </div>

          <aside className="lg:sticky lg:top-24">
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-2 text-[#1E3A8A] mb-4">
                <Calculator className="w-5 h-5" />
                <h3 className="font-heading text-lg font-medium">Tax Summary</h3>
              </div>

              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">2025 Final</p>
              <div className="divide-y divide-slate-100">
                <Row line="1a" label="Wholesale" gross={parseFloat(f.wholesale_2025) || 0} tax={t.l1a} muted />
                <Row line="1b" label="Retail" gross={parseFloat(f.retail_2025) || 0} tax={t.l1b} muted />
                <Row line="1c" label="Bus. Privilege" gross={parseFloat(f.biz_2025) || 0} tax={t.l1c} muted />
                <Row line="2" label="Total" tax={t.l2} />
                <Row line="3" label="Less est. paid" tax={t.l3} muted />
                <Row line="4" label="Tax due / credit" tax={t.l4} />
                <Row line="5" label="Penalty" tax={t.l5} muted />
                <Row line="6" label="Interest" tax={t.l6} muted />
                <Row line="7" label="2025 Total Due" tax={t.l7} />
              </div>

              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1 mt-5">2026 Estimated</p>
              <div className="divide-y divide-slate-100">
                <Row line="9" label="Estimated tax" tax={t.l9} />
                <Row line="10" label="Penalty" tax={t.l10} muted />
                <Row line="11" label="Interest" tax={t.l11} muted />
                <Row line="12" label="License fee" tax={t.l12} muted />
                <Row line="13" label="2026 Total Due" tax={t.l13} />
              </div>

              <div className="mt-5 bg-[#1E3A8A] text-white rounded-md p-4 flex items-center justify-between">
                <span className="text-sm">Line 14 · Total Due</span>
                <span className="font-mono-data text-xl font-semibold" data-testid="summary-total">
                  {money(t.l14)}
                </span>
              </div>

              {paymentsEnabled && t.l14 > 0 && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Payment method</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPayMethod("online")}
                      data-testid="paymethod-online"
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${
                        payMethod === "online"
                          ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A] font-medium"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <CreditCard className="w-4 h-4" /> Pay online
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayMethod("mail")}
                      data-testid="paymethod-mail"
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${
                        payMethod === "mail"
                          ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A] font-medium"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <Mail className="w-4 h-4" /> Mail a check
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || downloading}
                data-testid="submit-tax-filing"
                className="w-full mt-4 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : paymentsEnabled && payMethod === "online" && t.l14 > 0 ? (
                  <CreditCard className="w-4 h-4 mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                {paymentsEnabled && payMethod === "online" && t.l14 > 0
                  ? `Submit & Pay ${money(t.l14)}`
                  : "Submit Filing"}
              </Button>

              <Button
                type="button"
                variant="outline"
                disabled={submitting || downloading}
                onClick={downloadDraft}
                data-testid="download-draft-btn"
                className="w-full mt-2 border-slate-300"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Fill only — Download PDF (don't submit)
              </Button>

              <p className="text-xs text-slate-400 mt-3 text-center">
                {paymentsEnabled
                  ? "Submitting notifies the Finance Office. Choose to pay online now or mail a check."
                  : "Submitting notifies the Finance Office. Download your PDF and mail payment with it."}
              </p>
            </div>
          </aside>
        </form>
      </main>
    </div>
  );
}
